import { MatchingModel } from "../MatchingModel";
import { 
    AppEnum, 
    AppEnumHelper, 
    dateCompare, 
    DateFormatHelper, 
    DetailItem, 
    DetailSection, 
    DOMAIN_TYPES, 
    formatName, 
    MatchingEnums 
} from "@/Shared";

import { AccessRightModel, NeedViewModel, PropertyViewModel } from "@/Domains";
import { AuditViewModel } from "@/Shared/Logic/ViewModels/AuditViewModel";
import { AuditModel } from "@/Shared/Logic/Models/AuditModel";

export interface MatchingVMParams {
    matching: MatchingModel;
    viewEnums: MatchingEnums;
    accessRight: AccessRightModel;
    isDisplaying: boolean;
}

export class MatchingViewModel {

    public id: number;
    public model!: MatchingModel;
    public propertyId!: number;
    public needId!: number;
    public operationType!: string;
    public propertyCategory!: string;
    public propertyType!: string;
    public sectorName!: string;
    public cityName!: string;
    public priceRangeLabel!: string;
    public price!: number;
    public contactName!: string;
    public createdAt!: string;
    public status!: string;
    public property?: PropertyViewModel;
    public need?: NeedViewModel;

    //Technical
    public statusClass!: string;
    public audit!: AuditViewModel;

    public cardHeaderTitle!: string;
    public cardClass!: string;
    public isDeleted!: boolean;


    public constructor(private params: MatchingVMParams) {

        const { matching, viewEnums, accessRight, isDisplaying } = params;

        this.audit = new AuditViewModel(matching as AuditModel);

        this.id = matching?.id || 0;
        this.model = matching;

        this.needId = matching?.needId || 0;

        this.propertyId = matching?.propertyId || 0;

        const { propertyEnums, needEnums, MatchingStatusEnum } = viewEnums;

        this.property = matching.property ? new PropertyViewModel({
            property: matching.property,
            viewEnums: propertyEnums,
            accessRight,
            isDisplaying: false
        }) : undefined;

        this.need = matching.need ? new NeedViewModel({
            need : matching.need, 
            viewEnums :needEnums, 
            accessRight, 
            isDisplaying : false
         }) : undefined;

        this.operationType = AppEnumHelper.getLabel(propertyEnums.OperationTypeEnum, matching.property?.operationType);

        this.propertyType = AppEnumHelper.getLabel(propertyEnums.PropertyTypeEnum, matching.property?.propertyType);

        this.propertyCategory = AppEnumHelper.getLabel(propertyEnums.PropertyCategoryEnum, matching.property?.propertyCategory);

        this.sectorName = matching?.need?.sector?.name || "";

        this.cityName = matching?.need?.sector?.city?.name || "";

        this.contactName = formatName(matching?.need?.contact?.person);

        this.priceRangeLabel = AppEnumHelper.getLabel(propertyEnums.PriceRangeEnum, matching.property?.priceRange);

        this.createdAt = DateFormatHelper.toLocaleString(matching.createdAt) || "";

        this.initStatus(MatchingStatusEnum);

        this.cardHeaderTitle = this.propertyCategory + ' - ' + this.propertyType;
        this.isDeleted = !!matching.deletedAt
        this.cardClass = isDisplaying ? 'active' : '';
    }

    private initStatus(MatchingStatusEnum: AppEnum[]) {
        const statusEnum = MatchingStatusEnum.find(s => s.value === this.params.matching.status);

        this.status = statusEnum?.label || this.params.matching.status;
        this.statusClass = "matching-status--" + (statusEnum?.key ?? "").toUpperCase();
    }

    getAllSections = (): DetailSection[] => {
        const sections: DetailSection[] = [
            {
                sectionFn: this.getGlobalSection,
                legend: 'Opération',
                type: 'box'
            },
            {
                sectionFn: () => this.need?.getNeedSection() ?? [],
                legend: 'Besoin',
                type: 'box'
            },
            {
                sectionFn: this.getPropertySection,
                legend: 'Bien',
                type: 'box'
            }
        ];

        if (this.params.accessRight.canAudit(DOMAIN_TYPES.Matching)) {
            sections.push(
                {
                    sectionFn: this.audit.getAuditTrail,
                    legend: 'Traçabilité',
                    type: 'box'
                }
            );
        }
        return sections;
    }

    get title() {
        let title = "Rapp. N° " + this.id;
        if (this.propertyCategory.length > 0) {
            title += ' - ' + this.propertyCategory;
        }

        if (this.propertyType.length > 0) {
            title += ' | ' + this.propertyType;
        }
        return title;
    }

    getGlobalSection = () => {
        return [
            { label: "Prospect", value: this.contactName },
            { label: "Type d'opération", value: this.operationType },
            { label: "Budget", value: this.priceRangeLabel },
            { label: "Date création", value: this.createdAt },
            { label: "Statut", value: this.status },
        ] as DetailItem[];
    }

    getPropertySection = () => {

        const financialData = this.property?.getFinancialSection() || [];
        return [
            { label: "N° Bien", value: this.propertyId },
            { label: "Nature du bien", value: this.propertyCategory },
            { label: "Type du bien", value: this.propertyType },
            { label: "Ville", value: this.cityName },
            { label: "Secteur", value: this.sectorName },
            ...financialData,
            { label: "Date création", value: this.property?.createdAt },
        ] as DetailItem[];
    }

    compareTo(other: MatchingViewModel): number {
        const aEnum = AppEnumHelper.getByValue(
            this.params.viewEnums.MatchingStatusEnum,
            this.model.status
        );
        const bEnum = AppEnumHelper.getByValue(
            other.params.viewEnums.MatchingStatusEnum,
            other.model.status
        );

        const aIsInProgress = aEnum?.key === 'NOT_SATISFIED';
        const bIsInProgress = bEnum?.key === 'NOT_SATISFIED';

        if (aIsInProgress !== bIsInProgress) {
            return aIsInProgress ? -1 : 1;
        }

        return dateCompare(other.model.createdAt ?? "", this.model.createdAt ?? "");
    }
}